<template>
    <div class="relative" @pointerenter="handlePointerEnter" @pointerleave="handlePointerLeave" @focusin="openPreview"
        @focusout="handleFocusOut" @keydown.esc.stop="close">
        <button type="button" :class="['relative hdr_cart_btn carrito-trigger', { active: open }]"
            aria-label="Abrir carrito" @click.stop="togglePreview">
            <span
                class="absolute w-[22px] h-[22px] bg-secondary -top-[10px] -right-[11px] rounded-full flex items-center justify-center text-xs leading-none text-white">
                {{ cart.count }}
            </span>
            <svg class="fill-current text-title dark:text-white w-[18px] sm:w-[19px]" viewBox="0 0 19 23" fill="none"
                xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <path
                    d="M17.8284 5.7238H15.2408C14.9035 2.85886 12.4608 0.628906 9.50675 0.628906C6.55269 0.628906 4.11002 2.85886 3.7727 5.7238H1.18509C0.716102 5.7238 0.335938 6.10397 0.335938 6.57295V21.518C0.335938 21.987 0.716102 22.3671 1.18509 22.3671H17.8284C18.2974 22.3671 18.6776 21.987 18.6776 21.518V6.57295C18.6776 6.10397 18.2974 5.7238 17.8284 5.7238ZM9.50675 2.3272C11.5228 2.3272 13.2014 3.79857 13.5257 5.7238H5.48777C5.81214 3.79857 7.4907 2.3272 9.50675 2.3272ZM16.9793 20.6688H2.03424V7.4221H3.73253V9.96955C3.73253 10.4385 4.1127 10.8187 4.58168 10.8187C5.05067 10.8187 5.43083 10.4385 5.43083 9.96955V7.4221H13.5827V9.96955C13.5827 10.4385 13.9628 10.8187 14.4318 10.8187C14.9008 10.8187 15.281 10.4385 15.281 9.96955V7.4221H16.9793V20.6688Z" />
            </svg>
        </button>

        <div v-if="open"
            :class="[
                'hdr_cart_popup cart-preview z-50 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_24px_60px_rgba(15,23,42,0.14)]',
                'fixed left-2 right-2 top-[70px] sm:absolute sm:left-auto sm:right-0 sm:top-full sm:mt-3 sm:w-[390px]',
                { 'cart-active': open }
            ]">
            <div class="flex items-start justify-between gap-3 border-b border-slate-100 px-4 py-3">
                <div>
                    <div class="text-[11px] font-semibold uppercase tracking-[0.24em] text-secondary">Carrito</div>
                    <h4 class="mt-1 text-base font-semibold leading-tight text-title dark:text-white">Vista rápida</h4>
                </div>
                <div class="flex items-center gap-2">
                    <span
                        class="inline-flex min-w-[28px] items-center justify-center rounded-full bg-slate-100 px-2 py-1 text-[11px] font-semibold text-slate-600">
                        {{ cart.count }}
                    </span>
                    <button class="text-[11px] font-medium text-slate-500 hover:text-secondary" @click.stop="fetchCart"
                        :disabled="loading">
                        Actualizar
                    </button>
                </div>
            </div>

            <div v-if="loading && cart.items.length === 0" class="px-4 py-5 text-sm text-gray-600 dark:text-gray-200">
                Cargando...
            </div>

            <div v-else-if="cart.items.length === 0" class="px-4 py-5 text-sm leading-6 text-gray-600 dark:text-gray-200">
                <div class="font-semibold text-title dark:text-white">Tu carrito está vacío.</div>
                <p class="mt-1">Agrega productos, servicios, cupones u ofertas para verlos aquí.</p>
            </div>

            <div v-else class="cart-preview__list max-h-[calc(100vh-11rem)] overflow-y-auto px-3 py-2">
                <article v-for="item in cart.items" :key="item.key"
                    class="flex gap-3 rounded-xl px-2 py-2 transition hover:bg-slate-50">
                    <a :href="itemUrl(item)" class="block shrink-0">
                        <img :src="item.image || placeholder" alt=""
                            class="h-12 w-12 rounded-xl border border-slate-100 object-cover">
                    </a>

                    <div class="min-w-0 flex-1">
                        <div class="flex items-start justify-between gap-3">
                            <div class="min-w-0 flex-1">
                                <div class="flex flex-wrap items-center gap-2">
                                    <span
                                        class="inline-flex rounded-full bg-slate-100 px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">
                                        {{ typeLabel(item.type) }}
                                    </span>
                                    <span
                                        class="inline-flex rounded-full bg-secondary/10 px-2 py-1 text-[10px] font-semibold text-secondary">
                                        x{{ item.qty }}
                                    </span>
                                </div>

                                <a :href="itemUrl(item)"
                                    class="mt-2 block text-sm font-semibold leading-5 text-title transition hover:text-secondary">
                                    {{ item.name }}
                                </a>

                                <div class="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-slate-500">
                                    <span>Unit. ${{ format(item.price) }}</span>
                                    <span class="font-semibold text-title">Total ${{ format(itemTotal(item)) }}</span>
                                </div>
                            </div>

                            <button type="button"
                                class="shrink-0 text-[11px] font-medium text-slate-400 transition hover:text-red-600"
                                @click.stop="remove(item)" :disabled="isBusy(item)">
                                {{ isBusy(item) ? 'Quitando...' : 'Quitar' }}
                            </button>
                        </div>

                        <div class="mt-3 flex items-center justify-between gap-3">
                            <div class="inline-flex items-center rounded-full border border-slate-200 bg-white">
                                <button type="button"
                                    class="flex h-8 w-8 items-center justify-center rounded-l-full text-slate-500 transition hover:bg-slate-100 hover:text-title"
                                    @click.stop="dec(item)" :disabled="isBusy(item)" aria-label="Disminuir">
                                    <svg class="h-3 w-3" viewBox="0 0 14 2" fill="none" xmlns="http://www.w3.org/2000/svg"
                                        aria-hidden="true">
                                        <path d="M0.125 0.203613H13.8729V1.80309H0.125V0.203613Z" fill="currentColor" />
                                    </svg>
                                </button>
                                <span class="min-w-[28px] text-center text-[12px] font-semibold text-title">
                                    {{ item.qty }}
                                </span>
                                <button type="button"
                                    class="flex h-8 w-8 items-center justify-center rounded-r-full text-slate-500 transition hover:bg-slate-100 hover:text-title"
                                    @click.stop="inc(item)" :disabled="isBusy(item)" aria-label="Aumentar">
                                    <svg class="h-3 w-3" viewBox="0 0 14 14" fill="none"
                                        xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                                        <path
                                            d="M6.18017 0.110352H7.81774V6.16553H13.8729V7.76501H7.81774V13.8963H6.18017V7.76501H0.125V6.16553H6.18017V0.110352Z"
                                            fill="currentColor" />
                                    </svg>
                                </button>
                            </div>

                            <a :href="itemUrl(item)" class="text-[12px] font-semibold text-secondary hover:underline">
                                Ver detalle
                            </a>
                        </div>
                    </div>
                </article>
            </div>

            <div class="border-t border-slate-100 bg-slate-50/80 px-4 py-3">
                <div class="mb-3 flex items-center justify-between gap-3">
                    <span class="text-sm text-slate-500">Subtotal</span>
                    <span class="text-base font-semibold text-title">${{ format(cart.totals.subtotal) }}</span>
                </div>

                <div class="grid grid-cols-2 gap-3">
                    <button type="button"
                        class="inline-flex items-center justify-center rounded-full border border-slate-300 px-4 py-2 text-xs font-semibold text-title transition hover:border-slate-400 hover:bg-white"
                        @click.stop="clear" :disabled="clearing || cart.items.length === 0">
                        {{ clearing ? 'Vaciando...' : 'Vaciar' }}
                    </button>
                    <a :href="routes.checkout"
                        class="inline-flex items-center justify-center rounded-full bg-secondary px-4 py-2 text-xs font-semibold text-white transition hover:bg-title">
                        Ir a checkout
                    </a>
                </div>
            </div>
        </div>
    </div>

    <transition name="fade-slide">
        <div v-if="toast.visible" class="fixed top-16 right-6 z-[9999]">
            <div :class="[
                'min-w-[260px] max-w-sm px-4 py-3 rounded-lg shadow-lg flex items-start gap-3',
                toast.type === 'success' ? 'bg-green-600 text-white' : 'bg-red-600 text-white'
            ]">
                <svg v-if="toast.type === 'success'" xmlns="http://www.w3.org/2000/svg" class="w-5 h-5 mt-[2px]"
                    viewBox="0 0 20 20" fill="currentColor">
                    <path fill-rule="evenodd"
                        d="M16.707 5.293a1 1 0 0 1 0 1.414l-7.5 7.5a1 1 0 0 1-1.414 0l-3-3a1 1 0 1 1 1.414-1.414L8.5 12.086l6.793-6.793a1 1 0 0 1 1.414 0Z"
                        clip-rule="evenodd" />
                </svg>
                <svg v-else xmlns="http://www.w3.org/2000/svg" class="w-5 h-5 mt-[2px]" viewBox="0 0 20 20"
                    fill="currentColor">
                    <path fill-rule="evenodd" d="M18 10A8 8 0 1 1 2 10a8 8 0 0 1 16 0Zm-9-4h2v6H9V6Zm0 8h2v2H9v-2Z"
                        clip-rule="evenodd" />
                </svg>

                <div class="text-sm font-medium flex-1">
                    {{ toast.message }}
                </div>

                <button @click="hideToast" class="ml-2 opacity-80 hover:opacity-100">
                    <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" viewBox="0 0 20 20" fill="currentColor">
                        <path
                            d="M6.28 5.22a.75.75 0 1 0-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 1 0 1.06 1.06L10 11.06l3.72 3.72a.75.75 0 1 0 1.06-1.06L11.06 10l3.72-3.72a.75.75 0 1 0-1.06-1.06L10 8.94 6.28 5.22Z" />
                    </svg>
                </button>
            </div>
        </div>
    </transition>
</template>

<script>
import axios from 'axios';

export default {
    name: 'CartWidget',
    data() {
        return {
            toast: { visible: false, message: '', type: 'success', timeout: null },
            lastCount: 0,
            open: false,
            loading: false,
            clearing: false,
            busyKey: null,
            closeTimer: null,
            placeholder: '/images/placeholder.png',
            routes: {
                index: '/cart',
                add: '/cart/add',
                qty: '/cart/qty',
                remove: '/cart/remove',
                clear: '/cart/clear',
                page: '/checkout',
                checkout: '/checkout',
            },
            cart: {
                items: [],
                totals: { subtotal: 0, tax: 0, total: 0 },
                count: 0,
            },
            onCartUpdated: null,
            onCartItemAdded: null,
        };
    },
    mounted() {
        document.addEventListener('click', this.handleDocumentClick);
    },
    created() {
        this.fetchCart();

        this.onCartUpdated = (event) => {
            if (!event?.detail) {
                return;
            }

            const newCount = event.detail.count ?? 0;
            if (newCount > this.lastCount) {
                this.showToast('¡Producto agregado al carrito!');
            }

            this.cart = event.detail;
            this.lastCount = newCount;
        };

        this.onCartItemAdded = (event) => {
            const name = event?.detail?.name || 'Producto';
            this.showToast(`${name} agregado al carrito`);
        };

        window.addEventListener('cart:updated', this.onCartUpdated);
        window.addEventListener('cart:item-added', this.onCartItemAdded);
    },
    beforeUnmount() {
        document.removeEventListener('click', this.handleDocumentClick);
        this.clearCloseTimer();
        this.hideToast();

        if (this.onCartUpdated) {
            window.removeEventListener('cart:updated', this.onCartUpdated);
        }

        if (this.onCartItemAdded) {
            window.removeEventListener('cart:item-added', this.onCartItemAdded);
        }
    },
    methods: {
        handleDocumentClick(event) {
            if (!this.$el.contains(event.target)) {
                this.close();
            }
        },
        togglePreview() {
            if (this.open) {
                this.close();
            } else {
                this.openPreview();
            }
        },
        handlePointerEnter(event) {
            if (event.pointerType !== 'mouse') return;
            this.openPreview();
        },
        handlePointerLeave(event) {
            if (event.pointerType !== 'mouse') return;
            this.scheduleClose();
        },
        openPreview() {
            this.clearCloseTimer();
            this.open = true;
        },
        scheduleClose() {
            this.clearCloseTimer();
            this.closeTimer = window.setTimeout(() => {
                this.open = false;
            }, 120);
        },
        clearCloseTimer() {
            if (this.closeTimer) {
                window.clearTimeout(this.closeTimer);
                this.closeTimer = null;
            }
        },
        handleFocusOut(event) {
            if (!event.relatedTarget || !this.$el.contains(event.relatedTarget)) {
                this.scheduleClose();
            }
        },
        close() {
            this.clearCloseTimer();
            this.open = false;
        },
        async fetchCart() {
            this.loading = true;

            try {
                const { data } = await axios.get(this.routes.index);
                this.cart = data;
                this.lastCount = this.cart?.count ?? 0;
            } catch (error) {
                this.showToast('No se pudo cargar el carrito.', 'error');
            } finally {
                this.loading = false;
            }
        },
        showToast(message, type = 'success', duration = 2500) {
            this.toast.message = message;
            this.toast.type = type;
            this.toast.visible = true;

            if (this.toast.timeout) {
                clearTimeout(this.toast.timeout);
            }

            this.toast.timeout = setTimeout(() => {
                this.toast.visible = false;
                this.toast.timeout = null;
            }, duration);
        },
        hideToast() {
            if (this.toast.timeout) {
                clearTimeout(this.toast.timeout);
            }

            this.toast.visible = false;
            this.toast.timeout = null;
        },
        format(n) {
            return (n || 0).toLocaleString('es-CO');
        },
        typeLabel(type) {
            const labels = {
                cupon: 'Cupon',
                oferta: 'Oferta',
                producto: 'Producto',
                servicio: 'Servicio',
            };

            return labels[type] || type;
        },
        itemUrl(item) {
            const map = { producto: 'productos', servicio: 'servicios', cupon: 'cupones', oferta: 'ofertas' };
            const base = map[item.type] || 'items';
            return `/${base}/${item.id}`;
        },
        itemTotal(item) {
            return (item.price || 0) * (item.qty || 0);
        },
        isBusy(item) {
            return this.busyKey === item.key;
        },
        async updateQty(key, qty) {
            this.busyKey = key;

            try {
                const prev = this.cart.count;
                const { data } = await axios.patch(this.routes.qty, { key, qty });
                this.cart = data.cart;

                if (this.cart.count > prev) {
                    this.showToast('Cantidad actualizada en el carrito');
                }

                this.lastCount = this.cart.count;
            } catch (error) {
                this.showToast('No se pudo actualizar la cantidad.', 'error');
            } finally {
                this.busyKey = null;
            }
        },
        async remove(item) {
            this.busyKey = item.key;

            try {
                const { data } = await axios.delete(this.routes.remove, { data: { key: item.key } });
                this.cart = data.cart;
                this.lastCount = this.cart.count;
            } catch (error) {
                this.showToast('No se pudo eliminar el item.', 'error');
            } finally {
                this.busyKey = null;
            }
        },
        async clear() {
            this.clearing = true;

            try {
                const { data } = await axios.delete(this.routes.clear);
                this.cart = data.cart;
                this.lastCount = this.cart.count;
                this.showToast('Carrito vaciado');
            } catch (error) {
                this.showToast('No se pudo vaciar el carrito.', 'error');
            } finally {
                this.clearing = false;
            }
        },
        async dec(item) {
            const qty = Math.max(0, (item.qty || 0) - 1);
            await this.updateQty(item.key, qty);
        },
        async inc(item) {
            const qty = Math.max(1, (item.qty || 0) + 1);
            await this.updateQty(item.key, qty);
        },
    },
};
</script>

<style scoped>
.carrito-trigger {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-height: 44px;
    min-width: 44px;
    padding: 0.5rem;
    background: transparent;
    border: none;
    cursor: pointer;
    color: inherit;
}

.fade-slide-enter-active,
.fade-slide-leave-active {
    transition: all 0.25s ease;
    z-index: 100000;
}

.fade-slide-enter-from,
.fade-slide-leave-to {
    opacity: 0;
    transform: translateY(-8px);
}

.cart-preview__list::-webkit-scrollbar {
    width: 6px;
}

.cart-preview__list::-webkit-scrollbar-thumb {
    background: rgba(8, 156, 222, 0.26);
    border-radius: 5px;
}

.cart-preview__list::-webkit-scrollbar-track {
    background: transparent;
}
</style>
